import { createContext, useContext, useEffect, useState } from "react";
import { auth, provider } from "../../firebase"; // Your firebase.js
import {
  onAuthStateChanged,
  signOut,
  signInWithPopup,
  signInWithEmailAndPassword,
  signInWithCustomToken,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Snackbar, Alert } from "@mui/material";
import { useLocation } from "react-router-dom";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // "success", "error", "warning", "info"
  });

  const roleRoutes = {
    admin: "/admin",
    student: "/student",
    instructor: "/instructor",
    manager: "/manager",
  };

  // Helper function to show snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Keep localStorage token in sync whenever Firebase refreshes it.
  // Do NOT call getIdToken(true) here — that would force another refresh,
  // triggering this listener again in a loop.  Firebase fires onIdTokenChanged
  // *after* the refresh has already happened, so getIdToken() is fresh.
  useEffect(() => {
    const unsubscribe = auth.onIdTokenChanged(async (currentUser) => {
      if (currentUser) {
        try {
          const token = await currentUser.getIdToken();
          localStorage.setItem("backend_token", token);
        } catch (error) {
          console.error("Token sync error:", error);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Proactively refresh the Firebase ID token whenever the user returns to the
  // tab after a period of inactivity.  Browsers throttle background timers, so
  // Firebase's internal auto-refresh may not have fired.  Doing this on
  // visibilitychange means the stored token is already fresh before the user
  // clicks any sidebar link, preventing the 401 → blank-page cycle entirely.
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        try {
          await auth.authStateReady();
          const currentUser = auth.currentUser;
          if (currentUser) {
            const token = await currentUser.getIdToken(true); // force refresh
            localStorage.setItem("backend_token", token);
          }
        } catch (error) {
          // If we can't refresh the token, the interceptor in backendApis.js
          // will catch the subsequent 401 and redirect to /login.
          console.warn("Proactive token refresh failed:", error);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // --- Listen to Firebase Authentication Changes ---
  useEffect(() => {
    // Safety net: if Firebase never fires (network/init failure) unblock
    // protected routes after 5 s so ProtectedRoute can redirect to /login.
    const safetyTimer = setTimeout(() => {
      setLoading(false);
    }, 5000);

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      clearTimeout(safetyTimer);
      if (currentUser) {
        try {
          const token = await currentUser.getIdToken();
          localStorage.setItem("backend_token", token);
          const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/login`,
            { id_token: token },
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 10000,
            }
          );
          setUser(data.user);
        } catch (error) {
          console.error("Auth fetch error:", error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      clearTimeout(safetyTimer);
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const course = localStorage.getItem("course");

    // Prevent redirect loop if already on course details page
    const currentPath = location.pathname;

    const isCourseDetailsPage =
      currentPath === "/student/course_details";

    // If already on course details page → do NOT redirect again
    if (isCourseDetailsPage) {
      return;
    }

    const pathFrom = "/course_details";

    // If not on course details page, but course exists → redirect to respective course details
    if (user && user.role && course && pathFrom == location.pathname) {
      if (user.role === "student") {
        navigate("/student/course_details");
      }
      return;
    }

    // Existing behavior: Redirect based on role if coming from login/register/home
    const fromPaths = ["/", "/login", "/register"];
    if (user && user.role && fromPaths.includes(location.pathname)) {
      const redirectTimeout = setTimeout(() => {
        const path = roleRoutes[user.role] || "/";
        navigate(path);
      }, 1000);

      return () => clearTimeout(redirectTimeout);
    }

    const courseDetailPaths = [
      "/student/computer",
      "/student/refresher",
      "/student/driving",
      "/student/private",
      "/student/course_details",
      "/instructor/course_details",
      "/instructor/my-courses",
      "/instructor/schedule-students",
      "/computer_it_courses",
      "/computer_courses",
      "/refresher_courses",
      "/driving_courses",
      "/private_driving_courses",
      "/beginner_driving_courses",
      "/course_details",
      "/instructor",
      "/",
    ];

    const isNavigatingAway = !courseDetailPaths.includes(location.pathname);

    if (isNavigatingAway) {
      localStorage.removeItem("course");
    }

    // Cleanup function to run on unmount or route change
    return () => {
      if (isNavigatingAway) {
        localStorage.removeItem("course");
      }
    };
    // Clear course only when navigating away from course details pages
  }, [user, location.pathname, navigate]);

  // --- Regular Email/Password Login ---
  // Accepts email or phone number. Resolves the actual Firebase email via the
  // backend before calling Firebase, so phone-registered users with a real email
  // can still log in by phone.
  const handleLogin = async ({ emailOrPhone, password }) => {
    const input = (emailOrPhone || "").trim();

    // Resolve the actual email stored on the account
    let loginEmail;
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/resolve-login`,
        { identifier: input }
      );
      loginEmail = data.email;
    } catch {
      // Fallback: if no email is provided it could be a phone number
      // that was never registered — let Firebase surface the error
      loginEmail = input.includes("@") ? input : `${input}@moxy.co.ke`;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        loginEmail,
        password
      );
      await userCredential.user.getIdToken(); // refresh user state

      const token = await userCredential.user.getIdToken();
      localStorage.setItem("backend_token", token);

      // Fetch backend user immediately after login
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/login`,
        {
          id_token: token,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      localStorage.setItem("backend_token", data.api_auth_token);

      setUser(data.user); // <-- Set the user manually
      
      showSnackbar("Welcome Back", "success");
    } catch (error) {
      showSnackbar("No user found with the specified credentials", "error");
      throw error;
    }
  };

  // --- Google Login (with auto-register fallback for first-time users) ---
  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const token = await result.user.getIdToken();
      localStorage.setItem("backend_token", token);

      const authHeader = { Authorization: `Bearer ${token}` };

      try {
        // Attempt login with existing account
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/login`,
          { id_token: token },
          { headers: authHeader }
        );
        showSnackbar("Google sign-in successful!", "success");
        setUser(data.user);
      } catch (loginError) {
        // Only auto-register when the backend doesn't know this user yet
        const status = loginError.response?.status;
        if (status === 404 || status === 500) {
          const displayName = result.user.displayName || "";
          const parts = displayName.trim().split(/\s+/);
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/register`,
            {
              first_name: parts[0] || "User",
              last_name: parts.slice(1).join(" ") || ".",
              email: result.user.email,
              password: Math.random().toString(36).slice(-8),
            },
            { headers: authHeader }
          );
          // Fetch the full user object now that the account exists
          const { data: loginData } = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/login`,
            { id_token: token },
            { headers: authHeader }
          );
          showSnackbar("Google sign-in successful!", "success");
          setUser(loginData.user);
        } else {
          throw loginError;
        }
      }
    } catch (error) {
      console.error("Google Sign-In failed:", error);
      showSnackbar("Error signing in with Google. Please try again.", "error");
    }
  };

  // --- Sign in with Firebase custom token (for guest enrollment) ---
  const loginWithCustomToken = async (customToken) => {
    const result = await signInWithCustomToken(auth, customToken);
    const token = await result.user.getIdToken();
    localStorage.setItem("backend_token", token);

    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/login`,
      { id_token: token },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    localStorage.setItem("backend_token", data.api_auth_token);
    setUser(data.user);
    return data.user;
  };

  // --- Logout Function ---
  const logout = async () => {
    try {
      console.log("Starting logout...");

      localStorage.removeItem("backend_token");
      console.log("Removed backend token");

      await signOut(auth);
      console.log("Firebase signOut completed");

      setUser(null);
      console.log("User set to null");


      setTimeout(() => {
        navigate("/login");
        
      }, 1000); // Give time for snackbar to show
    } catch (error) {
      console.error("Logout error:", error);
      showSnackbar("Error during logout", "error");
    }
  };

  const value = {
    user,
    loading,
    handleLogin,
    handleGoogleSignIn,
    loginWithCustomToken,
    logout,
    setUser,
    showSnackbar,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ 
          vertical: 'top', 
          horizontal: 'right' 
        }}
        sx={{
          top: { xs: 16, sm: 24 }, // Responsive top spacing
          left: { xs: 16, sm: 24 }, // Responsive left spacing
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            minWidth: 300,
            maxWidth: 400,
            fontSize: '0.875rem',
            fontWeight: 500,
            // Custom styling for better visibility
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            '& .MuiAlert-icon': {
              fontSize: '1.25rem',
            },
            '& .MuiAlert-message': {
              padding: '8px 0',
            },
            '& .MuiAlert-action': {
              padding: '4px 0 4px 16px',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};